import type { VisitenkarteBefund } from "./visitenkarteZuFormState";

/**
 * vCard (.vcf) → Visitenkarten-Befund.
 *
 * Läuft VOLLSTÄNDIG im Browser: keine Texterkennung, kein Server, keine
 * Kosten — und die Datei verlässt den Rechner nicht. Eine vCard ist bereits
 * strukturiert; sie durch eine Bilderkennung zu schicken wäre absurd.
 *
 * Das Ergebnis ist bewusst derselbe `VisitenkarteBefund` wie beim
 * Kartenfoto. Damit teilen sich beide Wege die gesamte weitere Strecke:
 * Feldzuordnung, Prüfmaske, Katalogabgleich und die Verknüpfung von Person
 * und Firma.
 *
 * Unterstützt vCard 2.1, 3.0 und 4.0 — die drei Fassungen, die Outlook,
 * iOS-Kontakte, Android und die gängigen Kartenscanner-Apps exportieren.
 *
 * Privat oder geschäftlich (T-356): Ein Kontakt vom Handy ist oft privat. Was
 * die Karte ausdrücklich als privat führt (`TYPE=HOME`), landet in den
 * `…Privat`-Feldern. Eine Karte OHNE Firma (`ORG`) gilt als privater Kontakt:
 * Dann sind auch die Kanäle ohne Typ privat, und die Anschrift ist die
 * Wohnanschrift. Mit Firma bleibt es bei der Visitenkarten-Logik.
 */

/**
 * Aufgehobene Zeilenfaltung (RFC 6350 §3.2): Eine Fortsetzungszeile beginnt
 * mit einem Leerzeichen oder Tabulator und gehört an die vorige angehängt.
 * Exporte langer Adressen kommen praktisch immer gefaltet.
 */
const zeilenEntfalten = (text: string): string[] => {
  const roh = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const aus: string[] = [];
  for (const zeile of roh) {
    if (/^[ \t]/.test(zeile) && aus.length) aus[aus.length - 1] += zeile.slice(1);
    else aus.push(zeile);
  }
  return aus;
};

/** Maskierungen der Werteebene auflösen (RFC 6350 §3.4). */
const entmaskieren = (wert: string): string =>
  wert
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");

/**
 * Quoted-Printable dekodieren — nur vCard 2.1, aber genau die exportieren
 * ältere Outlook-Fassungen und viele Kartenscanner. Ohne das steht in der
 * Maske "Stra=C3=9Fe" statt "Straße".
 */
const quotedPrintable = (wert: string): string => {
  const bytes: number[] = [];
  for (let i = 0; i < wert.length; i++) {
    if (wert[i] === "=" && /^[0-9A-Fa-f]{2}$/.test(wert.slice(i + 1, i + 3))) {
      bytes.push(parseInt(wert.slice(i + 1, i + 3), 16));
      i += 2;
    } else if (wert[i] === "=" && wert[i + 1] === "\n") {
      i += 1; // weiche Zeilentrennung
    } else {
      bytes.push(wert.charCodeAt(i));
    }
  }
  try {
    return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
  } catch {
    return wert;
  }
};

type Eigenschaft = {
  name: string;
  /** Parameter in Kleinschreibung, z. B. { type: ["work", "voice"] }. */
  parameter: Record<string, string[]>;
  wert: string;
};

const eigenschaftLesen = (zeile: string): Eigenschaft | null => {
  const trenner = zeile.indexOf(":");
  if (trenner < 0) return null;

  const kopf = zeile.slice(0, trenner);
  let wert = zeile.slice(trenner + 1);

  const teile = kopf.split(";");
  // Gruppenpräfix ("item1.TEL") abschneiden — iOS nummeriert damit.
  const name = teile[0].replace(/^[^.]+\./, "").trim().toUpperCase();

  const parameter: Record<string, string[]> = {};
  for (const teil of teile.slice(1)) {
    const gleich = teil.indexOf("=");
    // vCard 2.1 schreibt Typen ohne Schlüssel ("TEL;WORK;VOICE:…").
    const schluessel = gleich < 0 ? "type" : teil.slice(0, gleich).trim().toLowerCase();
    const roh = gleich < 0 ? teil : teil.slice(gleich + 1);
    const werte = roh
      .split(",")
      .map((w) => w.replace(/^"|"$/g, "").trim().toLowerCase())
      .filter(Boolean);
    parameter[schluessel] = [...(parameter[schluessel] ?? []), ...werte];
  }

  if ((parameter.encoding ?? []).includes("quoted-printable")) wert = quotedPrintable(wert);

  return { name, parameter, wert };
};

const typen = (eig: Eigenschaft): string[] => eig.parameter.type ?? [];

const hatTyp = (eig: Eigenschaft, ...gesucht: string[]): boolean =>
  typen(eig).some((t) => gesucht.includes(t));

/** Feldliste eines strukturierten Werts ("N", "ADR"), Maskierung aufgelöst. */
const felder = (wert: string): string[] => {
  const aus: string[] = [];
  let aktuell = "";
  for (let i = 0; i < wert.length; i++) {
    if (wert[i] === "\\" && i + 1 < wert.length) {
      aktuell += wert[i] + wert[i + 1];
      i += 1;
    } else if (wert[i] === ";") {
      aus.push(entmaskieren(aktuell));
      aktuell = "";
    } else {
      aktuell += wert[i];
    }
  }
  aus.push(entmaskieren(aktuell));
  return aus;
};

const gesetzt = (wert: string | undefined): wert is string => !!wert && wert.trim() !== "";

/**
 * BDAY → `JJJJ-MM-TT` (Form des Datumsfelds der Maske). Erkannt werden
 * `1980-05-17`, `19800517` und dieselben Formen mit Uhrzeit. Ohne Jahr
 * (`--05-17`, `--0517`) oder mit dem Platzhalterjahr, das iOS per
 * `X-APPLE-OMIT-YEAR` kennzeichnet, gibt es kein vollständiges Datum —
 * dann `undefined`.
 */
export const geburtstagLesen = (wert: string, ohneJahr?: string): string | undefined => {
  const roh = wert.trim();
  const treffer = /^(\d{4})-?(\d{2})-?(\d{2})(?:$|T)/.exec(roh);
  if (!treffer) return undefined;
  const [, jahr, monat, tag] = treffer;
  if (ohneJahr && ohneJahr === jahr) return undefined;
  const d = new Date(Date.UTC(Number(jahr), Number(monat) - 1, Number(tag)));
  if (d.getUTCMonth() !== Number(monat) - 1 || d.getUTCDate() !== Number(tag)) return undefined;
  return `${jahr}-${monat}-${tag}`;
};

const setzeFalls = (
  befund: VisitenkarteBefund,
  schluessel: keyof VisitenkarteBefund,
  wert: string | undefined,
): void => {
  if (!gesetzt(wert)) return;
  if (gesetzt(befund[schluessel] as string | undefined)) return; // erster Treffer gewinnt
  (befund as Record<string, string>)[schluessel] = wert.trim();
};

/**
 * Karte ohne Firma = privater Kontakt (T-356): Die Kanäle ohne ausdrücklichen
 * Typ gehören der Person privat. Was die Karte ausdrücklich als geschäftlich
 * führt (`TYPE=WORK`), bleibt geschäftlich. Ist das private Feld schon belegt,
 * bleibt der andere Wert geschäftlich stehen, statt still zu verschwinden.
 */
const privatUmbuchen = (
  befund: VisitenkarteBefund,
  geschaeftlich: ReadonlySet<keyof VisitenkarteBefund>,
): void => {
  const paare: Array<[keyof VisitenkarteBefund, keyof VisitenkarteBefund]> = [
    ["telefon", "telefonPrivat"],
    ["mobil", "mobilPrivat"],
    ["email", "emailPrivat"],
  ];
  for (const [von, nach] of paare) {
    if (!gesetzt(befund[von]) || gesetzt(befund[nach]) || geschaeftlich.has(von)) continue;
    (befund as Record<string, string>)[nach] = befund[von] as string;
    delete befund[von];
  }
};

export type VcardErgebnis = {
  befund: VisitenkarteBefund;
  /** Wie viele Karten die Datei enthält — mehr als eine wird gemeldet. */
  anzahl: number;
};

/**
 * Liest die ERSTE Karte einer .vcf-Datei.
 *
 * Sammelexporte mit mehreren hundert Kontakten sind verbreitet; sie hier
 * stillschweigend auf den ersten Eintrag zu reduzieren, wäre eine Falle.
 * Deshalb wird die Gesamtzahl mitgegeben, damit der Dialog sie anzeigen kann.
 */
export const vcardLesen = (text: string): VcardErgebnis => {
  const zeilen = zeilenEntfalten(text);
  const anzahl = zeilen.filter((z) => /^BEGIN:VCARD/i.test(z.trim())).length;

  const befund: VisitenkarteBefund = {};
  let inKarte = false;
  // Erst wenn eine bevorzugte Quelle fehlt, greifen die Rückfallebenen.
  let fnName = "";
  let orgName = "";
  let telefonOhneTyp = "";
  let emailOhneTyp = "";
  /** Felder, die die Karte AUSDRÜCKLICH geschäftlich führt (`TYPE=WORK`). */
  const geschaeftlich = new Set<keyof VisitenkarteBefund>();
  /** Erste private Anschrift (`TYPE=HOME`) — Wohnanschrift einer Karte ohne Firma. */
  let heim: string[] | null = null;

  for (const zeile of zeilen) {
    const getrimmt = zeile.trim();
    if (/^BEGIN:VCARD/i.test(getrimmt)) {
      if (inKarte) break; // zweite Karte — die erste ist vollständig
      inKarte = true;
      continue;
    }
    if (!inKarte) continue;
    if (/^END:VCARD/i.test(getrimmt)) break;

    const eig = eigenschaftLesen(zeile);
    if (!eig || !gesetzt(eig.wert)) continue;

    switch (eig.name) {
      case "N": {
        // N:Nachname;Vorname;weitere;Präfix;Suffix
        const [nachname, vorname, , praefix] = felder(eig.wert);
        setzeFalls(befund, "nachname", nachname);
        setzeFalls(befund, "vorname", vorname);
        if (gesetzt(praefix)) {
          // Im Präfix steht meist die Anrede, manchmal auch der Titel.
          const anrede = praefix.match(/\b(Herrn?|Frau)\b/i)?.[1];
          if (anrede) setzeFalls(befund, "anredeText", /^h/i.test(anrede) ? "Herr" : "Frau");
          const titel = praefix.replace(/\b(Herrn?|Frau)\b/gi, "").trim();
          if (titel) setzeFalls(befund, "titel", titel);
        }
        break;
      }
      case "FN":
        fnName = entmaskieren(eig.wert).trim();
        break;
      case "ORG": {
        // ORG:Firma;Abteilung — die Abteilung ist nicht der Firmenname.
        orgName = felder(eig.wert)[0]?.trim() ?? "";
        break;
      }
      case "TITLE":
        setzeFalls(befund, "position", entmaskieren(eig.wert));
        break;
      case "ROLE":
        // ROLE ist die Rückfallebene, wenn TITLE fehlt (Android exportiert so).
        setzeFalls(befund, "position", entmaskieren(eig.wert));
        break;
      case "TEL": {
        const wert = entmaskieren(eig.wert).trim();
        if (hatTyp(eig, "fax")) setzeFalls(befund, "fax", wert);
        // Ausdrücklich privat (iOS: „type=HOME"), nicht zugleich geschäftlich.
        else if (hatTyp(eig, "home") && !hatTyp(eig, "work")) {
          setzeFalls(befund, hatTyp(eig, "cell", "mobile") ? "mobilPrivat" : "telefonPrivat", wert);
        }
        else if (hatTyp(eig, "cell", "mobile")) {
          if (hatTyp(eig, "work") && !gesetzt(befund.mobil)) geschaeftlich.add("mobil");
          setzeFalls(befund, "mobil", wert);
        } else if (hatTyp(eig, "work", "voice", "main")) {
          if (hatTyp(eig, "work") && !gesetzt(befund.telefon)) geschaeftlich.add("telefon");
          setzeFalls(befund, "telefon", wert);
        }
        else if (!telefonOhneTyp) telefonOhneTyp = wert;
        break;
      }
      case "EMAIL": {
        const wert = entmaskieren(eig.wert).trim().replace(/^mailto:/i, "");
        if (hatTyp(eig, "home") && !hatTyp(eig, "work")) setzeFalls(befund, "emailPrivat", wert);
        else if (hatTyp(eig, "work", "internet", "pref")) {
          if (hatTyp(eig, "work") && !gesetzt(befund.email)) geschaeftlich.add("email");
          setzeFalls(befund, "email", wert);
        }
        else if (!emailOhneTyp) emailOhneTyp = wert;
        break;
      }
      case "ADR": {
        // ADR:Postfach;Zusatz;Straße;Ort;Region;PLZ;Land
        const [, , strasse, ort, , plz, land] = felder(eig.wert);
        if (hatTyp(eig, "home") && !heim) heim = [strasse, ort, plz, land];
        // Eine private Anschrift überschreibt keine geschäftliche.
        if (hatTyp(eig, "home") && gesetzt(befund.strasse)) break;
        setzeFalls(befund, "strasse", strasse);
        setzeFalls(befund, "ort", ort);
        setzeFalls(befund, "plz", plz);
        setzeFalls(befund, "land", land);
        break;
      }
      case "BDAY": {
        const omit = eig.parameter["x-apple-omit-year"]?.[0];
        setzeFalls(befund, "geburtsdatum", geburtstagLesen(entmaskieren(eig.wert), omit));
        break;
      }
      case "URL": {
        const wert = entmaskieren(eig.wert).trim();
        if (/linkedin\.com/i.test(wert)) setzeFalls(befund, "linkedin", wert);
        else if (/xing\.com/i.test(wert)) setzeFalls(befund, "xing", wert);
        else setzeFalls(befund, "website", wert);
        break;
      }
      case "X-SOCIALPROFILE": {
        const wert = entmaskieren(eig.wert).trim();
        if (hatTyp(eig, "linkedin") || /linkedin\.com/i.test(wert)) {
          setzeFalls(befund, "linkedin", wert);
        } else if (hatTyp(eig, "xing") || /xing\.com/i.test(wert)) {
          setzeFalls(befund, "xing", wert);
        }
        break;
      }
      default:
        break;
    }
  }

  if (!gesetzt(befund.telefon) && telefonOhneTyp) befund.telefon = telefonOhneTyp;
  if (!gesetzt(befund.email) && emailOhneTyp) befund.email = emailOhneTyp;
  if (gesetzt(orgName)) setzeFalls(befund, "firma", orgName);
  else {
    privatUmbuchen(befund, geschaeftlich);
    // Ohne Firma ist die private Anschrift die Wohnanschrift — sie gewinnt.
    if (heim && (heim as string[]).some(gesetzt)) {
      const [strasse, ort, plz, land] = heim as string[];
      for (const k of ["strasse", "ort", "plz", "land"] as const) delete befund[k];
      setzeFalls(befund, "strasse", strasse);
      setzeFalls(befund, "ort", ort);
      setzeFalls(befund, "plz", plz);
      setzeFalls(befund, "land", land);
    }
  }

  // FN ist der Anzeigename ("Dr. Erika Mustermann") und nur dann die beste
  // Quelle, wenn N fehlt — was bei Exporten aus Webdiensten vorkommt.
  if (!gesetzt(befund.nachname) && gesetzt(fnName)) {
    const teile = fnName.split(/\s+/).filter(Boolean);
    if (teile.length === 1) {
      befund.nachname = teile[0];
    } else if (teile.length > 1) {
      befund.nachname = teile[teile.length - 1];
      befund.vorname = teile.slice(0, -1).join(" ");
    }
  }

  return { befund, anzahl };
};

/** Ist das plausibel eine vCard? Verhindert, dass ein Fehlgriff als leer gilt. */
export const istVcard = (text: string): boolean => /BEGIN:VCARD/i.test(text);
